import React, {useState, useEffect, useRef} from 'react';
import './MSNPage.css';
import type {Team} from './AccueilLayout';

/* ─── Wobbly SVG rectangle (hand-drawn look) ─── */
function SketchRect({className, children}: { className?: string; children?: React.ReactNode }) {
    return (
        <div className={`sketch-box ${className || ''}`}>
            <svg className="sketch-box__border" viewBox="0 0 400 300" preserveAspectRatio="none">
                <path
                    d="M18,8 C90,5 200,10 382,8 C386,10 390,14 392,18 C394,80 390,200 392,282 C390,286 386,290 382,292 C280,295 100,290 18,292 C14,290 10,286 8,282 C6,180 10,60 8,18 C10,14 14,10 18,8 Z"
                    fill="none" stroke="#222" strokeWidth="2.5"
                />
            </svg>
            <div className="sketch-box__content">{children}</div>
        </div>
    );
}

/* Smaller, wider variant for the input box */
function SketchRectInput({className, children}: { className?: string; children?: React.ReactNode }) {
    return (
        <div className={`sketch-box ${className || ''}`}>
            <svg className="sketch-box__border" viewBox="0 0 400 100" preserveAspectRatio="none">
                <path
                    d="M20,6 C120,4 280,8 380,6 C385,9 389,14 391,20 C393,40 390,65 392,80 C390,85 385,90 380,93 C260,95 140,92 20,94 C15,91 11,86 9,80 C7,55 10,30 8,20 C10,14 15,9 20,6 Z"
                    fill="none" stroke="#222" strokeWidth="2.5"
                />
            </svg>
            <div className="sketch-box__content">{children}</div>
        </div>
    );
}

/* Square frame for avatars / MSN icon */
function SketchSquare({className, children}: { className?: string; children?: React.ReactNode }) {
    return (
        <div className={`sketch-square ${className || ''}`}>
            <svg className="sketch-square__border" viewBox="0 0 100 100" preserveAspectRatio="none">
                <path
                    d="M12,6 C30,4 70,8 88,6 C92,9 96,13 97,18 C99,40 96,70 98,82 C96,87 92,92 88,94 C65,96 35,93 12,95 C8,92 5,87 4,82 C3,55 5,30 3,18 C5,13 8,9 12,6 Z"
                    fill="none" stroke="#222" strokeWidth="2.8"
                />
            </svg>
            <div className="sketch-square__content">{children}</div>
        </div>
    );
}

/* ─── Chat messages (sender: 'a' = first person, 'b' = second person) ─── */
const MESSAGES_RAW = [
    {who: 'b', text: 'salut'},
    {who: 'a', text: 'coucou'},
    {who: 'a', text: 'quoi de beau ??'},
    {who: 'b', text: 'que du moche'},
    {who: 'a', text: "c'est marrant"},
    {who: 'b', text: 'tu penses que nos invités vont venir à notre PACS le 6 juin ?'},
];

const DELAY_MS = 1800;

export default function MSNPage({team = 'edouard'}: {team?: Team}) {
    /* Edouard's view: a=Ed, b=JuBu | Juliette's view: a=JuBu, b=Ed */
    const names = team === 'juliette'
        ? {a: 'JuBu_du94', b: 'Ed_le_BGÉ'}
        : {a: 'Ed_le_BGÉ', b: 'JuBu_du94'};
    const MESSAGES = MESSAGES_RAW.map(m => ({sender: names[m.who as 'a' | 'b'], text: m.text}));
    const [visibleCount, setVisibleCount] = useState(0);
    const allShown = visibleCount >= MESSAGES.length;
    const chatEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (visibleCount >= MESSAGES.length) return;
        const timer = setTimeout(
            () => setVisibleCount(c => c + 1),
            visibleCount === 0 ? 700 : DELAY_MS
        );
        return () => clearTimeout(timer);
    }, [visibleCount]);

    /* Auto-scroll to bottom when a new message appears */
    useEffect(() => {
        chatEndRef.current?.scrollIntoView({behavior: 'smooth'});
    }, [visibleCount]);

    return (
        <div className="msn-page">
            <div className="msn-layout">

                {/* ── Left: MSN logo ── */}
                <div className="msn-logo-col">
                    <SketchSquare className="msn-logo-box">
                        <img src="/msn.png" alt="MSN" className="msn-logo__img"/>
                    </SketchSquare>
                </div>

                {/* ── Center: chat + input ── */}
                <div className="msn-center">
                    <SketchRect className="msn-chat">
                        <div className="msn-chat__messages">
                            {MESSAGES.slice(0, visibleCount).map((m, i) => (
                                <div key={i} className="msn-msg msn-msg--appear">
                                    <span className="msn-msg__sender">{m.sender} dit :</span>
                                    <span className="msn-msg__text">{m.text}</span>
                                </div>
                            ))}
                            <div ref={chatEndRef}/>
                        </div>
                    </SketchRect>

                    <SketchRectInput className="msn-input-box">
                        {!allShown && <span className="msn-cursor">|</span>}
                        {allShown && (
                            <div className="msn-rsvp msn-rsvp--appear">
                                <a className="msn-rsvp__btn msn-rsvp__btn--yes" href="https://doodle.com/sign-up-sheet/participate/75ec0e6b-e832-4c5a-887c-1a51655cf0cb/select" target="_blank" rel="noopener noreferrer">J'y serai</a>
                                <button className="msn-rsvp__btn msn-rsvp__btn--no">Je peux pas</button>
                            </div>
                        )}
                    </SketchRectInput>
                </div>

                {/* ── Right: avatars ── */}
                <div className="msn-right">
                    <div className="msn-avatar-block">
                        <SketchSquare className="msn-avatar-box">
                            <img src={team === 'juliette' ? '/edbe.png' : '/jubu.png'} alt={team === 'juliette' ? 'EdBe' : 'JuBu'} className="msn-avatar__img"/>
                        </SketchSquare>
                        <span className="msn-avatar__name">{team === 'juliette' ? 'Ed_le_BGÉ' : 'JuBu_du94'}</span>
                    </div>

                    <div className="msn-avatar-block">
                        <SketchSquare className="msn-avatar-box">
                            <img src={team === 'juliette' ? '/jubu.png' : '/edbe.png'} alt={team === 'juliette' ? 'JuBu' : 'EdBe'} className="msn-avatar__img"/>
                        </SketchSquare>
                        <span className="msn-avatar__name">{team === 'juliette' ? 'JuBu_du94' : 'Ed_le_BGÉ'}</span>
                    </div>
                </div>

            </div>
        </div>
    );
}
